/// Copyright (C) Microsoft Corporation. All rights reserved.
import { AnyObject } from '@mecontrol/common';
import { hasOwn } from './utilities';

export interface IComponentProxy<T> {
    /**
     * Sets the component instance at which point we trigger any queued requests,
     * and stop queuing them for future requests. Subsequent requests are delegated
     * directly to the component
     *
     * @param {T} component The controller instance that will receive delegated requests
     */
    setComponent(component: T): void;

    /**
     * Handles a single request by delegating or queuing it depending on the component being ready.
     *
     * @param {keyof T} name The name of the function to be called on the component
     * @param {boolean} priority Optional flag to indicate this method's calls should be prioritized
     * @param {any[]} params The parameters to be passed into the delegated function
     */
    handleCall: (name: keyof T, priority?: boolean) => (...params: any[]) => void;
}

interface IQueuedCall<M> {
    name: keyof M;
    params: any[];
}

/**
 * Creates a class to delegate calls to an object that will be delay-loaded.
 * The type of the object being proxied is the generic parameter.
 */
export function createProxy<T extends AnyObject>(): IComponentProxy<T> {
    let _component: T;
    let _queuedCalls: IQueuedCall<T>[] = [];
    let _priorityPos = 0;

    function setComponent(component: T): void {
        _component = component;

        let queuedCalls = _queuedCalls;
        _queuedCalls = [];
        for (let i = 0; i < queuedCalls.length; i++) {
            triggerCall(queuedCalls[i]);
        }
    }

    const handleCall = (name: keyof T, priority: boolean = false) => (...params: any[]) => {
        let qCall: IQueuedCall<T> = { name, params };
        if (_component) {
            triggerCall(qCall);
        }
        else {
            // Queue up a component call for the proxy. Handles prioritizing important calls by inserting them
            // before the first non-priority calls in the queue.
            _queuedCalls.splice(priority ? _priorityPos++ : _queuedCalls.length, 0, qCall);
        }
    };

    function triggerCall(queuedCall: IQueuedCall<T>): void {
        let apiName = queuedCall.name as string;
        if (_component && hasOwn(_component, apiName)) {
            _component[apiName].apply(_component, queuedCall.params);
        }
    }

    return {
        setComponent,
        handleCall,
    };
}
